export type SameSiteRequestHeaders = Record<string, string | string[] | undefined>;

function readHeader(headers: SameSiteRequestHeaders, name: string): string | null {
  const value = headers[name];
  if (Array.isArray(value)) return value[0] ?? null;
  return typeof value === 'string' ? value : null;
}

export function getRequestOrigin(headers: SameSiteRequestHeaders): string | null {
  const host = readHeader(headers, 'host');
  if (!host) return null;
  const proto = readHeader(headers, 'x-forwarded-proto') ?? 'http';
  return `${proto}://${host}`;
}

export function assertSameSiteRequest(
  headers: SameSiteRequestHeaders,
  appBaseUrl?: string,
): boolean {
  const fetchSite = readHeader(headers, 'sec-fetch-site');
  if (fetchSite !== null) {
    return fetchSite === 'same-origin' || fetchSite === 'none';
  }
  const origin = readHeader(headers, 'origin');
  if (origin === null) return false;

  const expected = getRequestOrigin(headers);
  if (expected !== null && origin === expected) return true;

  if (appBaseUrl) {
    try {
      const configured = new URL(appBaseUrl);
      const host = readHeader(headers, 'host');
      if (host === configured.host && origin === configured.origin) {
        return true;
      }
    } catch {
      return false;
    }
  }

  return false;
}

export function parseRequestBody(body: unknown): unknown {
  if (body == null) return body;
  if (typeof body === 'string') {
    if (!body.trim()) return null;
    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(body)) {
    try {
      return JSON.parse(body.toString('utf8'));
    } catch {
      return null;
    }
  }
  return body;
}

export function assertSameSiteFetchRequest(req: Request, appBaseUrl?: string): boolean {
  const fetchSite = req.headers.get('sec-fetch-site');
  if (fetchSite !== null) {
    return fetchSite === 'same-origin' || fetchSite === 'none';
  }
  const origin = req.headers.get('origin');
  if (origin === null) return false;
  const host = req.headers.get('host');
  if (!host) return false;
  const proto = req.headers.get('x-forwarded-proto') ?? new URL(req.url).protocol.replace(':', '');
  if (origin === `${proto}://${host}`) return true;

  if (appBaseUrl) {
    try {
      const configured = new URL(appBaseUrl);
      if (host === configured.host && origin === configured.origin) {
        return true;
      }
    } catch {
      return false;
    }
  }

  return false;
}

function isValidJwtShape(token: string): boolean {
  const parts = token.split('.');
  if (parts.length < 2) return false;
  try {
    const payload = JSON.parse(
      Buffer.from(parts[1].replace(/-/g, '+').replace(/_/g, '/'), 'base64').toString('utf8'),
    ) as { exp?: unknown };
    return typeof payload.exp === 'number' && payload.exp > 0;
  } catch {
    return false;
  }
}

export function readBodySessionTokens(
  body: unknown,
): { sessionToken: string; refreshToken: string } | null {
  if (!body || typeof body !== 'object') return null;
  const sessionToken = (body as { sessionToken?: unknown }).sessionToken;
  const refreshToken = (body as { refreshToken?: unknown }).refreshToken;
  if (typeof sessionToken !== 'string' || !sessionToken.trim()) return null;
  if (typeof refreshToken !== 'string' || !refreshToken.trim()) return null;
  if (!isValidJwtShape(sessionToken)) return null;
  return { sessionToken: sessionToken.trim(), refreshToken: refreshToken.trim() };
}
